import { Await, Link } from '@remix-run/react'
import Button from '../buttons/button'
import { Counter, Tag } from '../badgesAndTags'
import Loader from '../loaders/loader'
import { TypographyHeading, TypographyLabel } from '../typography'
import {
	CalloutSuccessIcon,
	CalloutWarningIcon,
	CopyIcon,
	XIcon,
	CaretLeft,
} from '../icons'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { Suspense, useRef } from 'react'
import ProofOfExhibitionTemplate from '../common/metadataTemplates/proofOfExhibitionTemplate.tsx'
import MediaCitationTemplate from '../common/metadataTemplates/mediaCitationTemplate.tsx'
import CommentTemplate from '../common/metadataTemplates/commentTemplate.tsx'
import TweetTemplate from '../common/metadataTemplates/tweetTemplate.tsx'
import InstagramTemplate from '../common/metadataTemplates/instagramTemplate.tsx'
import OffchainLendTemplate from '../common/metadataTemplates/offchainLendTemplate.tsx'
import OffchainSaleTemplate from '../common/metadataTemplates/offchainSaleTemplate.tsx'
import CountersignTemplate from '../common/metadataTemplates/countersignTemplate.tsx'
import CIDsTable from '../common/cidsTable.tsx'
import SignersTable from '../common/signersTable.tsx'
import CastTemplate from '../common/metadataTemplates/castTemplate.tsx'
import { AttestationType } from '#types/loreTypes.tsx'
import { nft } from '#types/loreTypes.tsx'
import {
	formatMetadataType,
	getEventURL,
	getNFTProfileURL,
	getShareAttestationToXURL,
	mapIcon,
	mapSigners,
	mapStatus,
} from '#app/utils/misc.tsx'
import RevokeTemplate from '../common/metadataTemplates/revokeTemplate.tsx'
import BackButton from '../common/backButton.tsx'
import InternalLink from '../common/internalLink.tsx'
import ContentTile from '../content/ContentTile.tsx'
import BulkTemplate from '../common/metadataTemplates/bulkTemplate.tsx'
import MediaUploadTemplate from '../common/metadataTemplates/mediaUploadTemplate.tsx'

const formatDate = (date: Date) => {
	try {
		const options = {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		}
		//@ts-ignore
		return date.toLocaleDateString('en-US', options)
	} catch (e) {
		return 'Unknown'
	}
}

export default function MetadataProfile({
	nftData,
	cid,
	metadataEntry,
	rollupAttestation,
	atomicLoreUrl,
}: {
	nftData: nft
	cid: string
	metadataEntry: AttestationType
	rollupAttestation?: AttestationType
	atomicLoreUrl: string
}) {
	const tooltipCopyIPFS = useRef<TooltipRefProps>(null)
	const tooltipCopyArweave = useRef<TooltipRefProps>(null)

	return (
		<div className="mx-auto mb-16 flex h-full w-full max-w-[1440px] flex-1 flex-col px-4 md:px-12 lg:px-28">
			<Suspense
				fallback={
					<div className="mt-28">
						<TypographyHeading
							size="large"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{`Loading metadata entry ${nftData ? `for ${nftData.name}...` : '...'}`}
						</TypographyHeading>
						<Loader size="medium" />
					</div>
				}
			>
				<Await resolve={metadataEntry}>
					{metadataEntry => {
						return (
							<>
								<div className="hidden md:block">
									<div className="mb-8 mt-16 flex flex-col items-start justify-around gap-y-4 pl-0 md:items-start lg:justify-evenly">
										<InternalLink
											to={getNFTProfileURL(nftData)}
											prefetch="none"
										>
											<Button variant="tertiary">
												<div className="flex items-center">
													<CaretLeft />
													NFT History
												</div>
											</Button>
										</InternalLink>
									</div>
									<div className="w-fit">
										{!metadataEntry.isRevoked && (
											<Tag
												icon={mapIcon(
													metadataEntry.template.slug,
													'large',
													metadataEntry.properties?.fileType || '',
												)}
												showIcon={true}
											>
												{formatMetadataType(
													metadataEntry.template.slug,
													metadataEntry.template.name,
												)}
											</Tag>
										)}
									</div>
									<div className="mt-4 flex flex-row items-center justify-between">
										<TypographyHeading
											size="large"
											isBold={true}
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>{`${metadataEntry.isRevoked ? 'Revoked ' : ''}${formatMetadataType(metadataEntry.template.slug, metadataEntry.template.name)} - ${nftData.name}`}</TypographyHeading>
										<div className="flex w-full flex-row justify-end gap-x-2">
											{!metadataEntry.isRevoked && (
												<Link
													to={getShareAttestationToXURL(
														metadataEntry,
														atomicLoreUrl,
														nftData,
													)}
													target="_blank"
												>
													<Button variant="secondary" leftIcon={<XIcon />}>
														Share
													</Button>
												</Link>
											)}
											<InternalLink
												to={getNFTProfileURL(nftData)}
												prefetch="none"
											>
												<Button variant="primary">View token profile</Button>
											</InternalLink>
											{metadataEntry.properties &&
												metadataEntry.properties.rollupAttestationCID && (
													<InternalLink
														to={`/metadataProfile/rollup/${metadataEntry.properties.rollupAttestationCID}`}
														prefetch="none"
													>
														<Button variant="primary">
															View roll-up attestation
														</Button>
													</InternalLink>
												)}
										</div>
									</div>
								</div>
								{!metadataEntry.isRevoked && (
									<div className="md:hidden">
										<div className="mb-8 mt-16 flex flex-col items-start justify-around gap-y-4 pl-0 md:items-start lg:justify-evenly">
											<BackButton />
										</div>
										<div className="w-fit">
											<Tag
												icon={mapIcon(
													metadataEntry.template.slug,
													'large',
													metadataEntry.properties?.fileType || '',
												)}
												showIcon={true}
											>
												{formatMetadataType(metadataEntry.template.slug)}
											</Tag>
										</div>
										<div className="mt-4 flex flex-row items-center justify-start">
											<TypographyHeading
												size="large"
												isBold={true}
											>{`${metadataEntry.isRevoked ? 'Revoked ' : ''}${formatMetadataType(metadataEntry.template.slug)} - ${nftData.name}`}</TypographyHeading>
										</div>
									</div>
								)}
								{!metadataEntry.isRevoked && metadataEntry.createdAt && (
									<div className="mt-4 flex flex-row items-center gap-x-2">
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Timestamp:{' '}
										</TypographyLabel>
										<TypographyLabel
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{formatDate(new Date(metadataEntry.createdAt))}
										</TypographyLabel>
									</div>
								)}
								{!metadataEntry.isRevoked && (
									<div className="mt-4 flex flex-col items-center gap-x-2 sm:flex-row">
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
										>
											IPFS CID:
										</TypographyLabel>
										<TypographyLabel
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{cid}
										</TypographyLabel>
										<div id="copyIPFS" className="flex flex-row">
											<CopyIcon
												className="h-4 w-4 text-textSecondaryLight hover:cursor-pointer dark:text-textSecondaryDark"
												onClick={() => {
													navigator.clipboard.writeText(cid)
													tooltipCopyIPFS.current?.open({
														anchorSelect: '#copyIPFS',
														content: 'Copied!',
													})
													setTimeout(() => {
														tooltipCopyIPFS.current?.close()
													}, 2000)
												}}
											/>
											<Tooltip imperativeModeOnly ref={tooltipCopyIPFS} />
										</div>
									</div>
								)}
								{!metadataEntry.isRevoked && (
									<div className="mt-4 flex flex-col items-center gap-x-2 sm:flex-row">
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Arweave Transaction:
										</TypographyLabel>
										<TypographyLabel
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark truncate"
										>
											{metadataEntry.references.attestationArweaveTxId}
										</TypographyLabel>
										<div id="copyArweave" className="flex flex-row">
											<CopyIcon
												className="h-4 w-4 text-textSecondaryLight hover:cursor-pointer dark:text-textSecondaryDark"
												onClick={() => {
													navigator.clipboard.writeText(
														metadataEntry.references.attestationArweaveTxId
															? metadataEntry.references.attestationArweaveTxId
															: '',
													)
													tooltipCopyArweave.current?.open({
														anchorSelect: '#copyArweave',
														content: 'Copied!',
													})
													setTimeout(() => {
														tooltipCopyArweave.current?.close()
													}, 2000)
												}}
											/>
											<Tooltip imperativeModeOnly ref={tooltipCopyArweave} />
										</div>
									</div>
								)}
								{metadataEntry.template.slug === 'proofOfExhibition' && (
									<div className="mt-4 flex flex-row items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
										>
											Status:
										</TypographyLabel>
										{mapStatus(metadataEntry) == 'Pending' ? (
											<CalloutWarningIcon />
										) : (
											<CalloutSuccessIcon />
										)}
										<TypographyLabel size="medium">
											{mapStatus(metadataEntry)}
										</TypographyLabel>
									</div>
								)}
								{metadataEntry.template.slug === 'offchainLend' && (
									<div className="mt-4 flex flex-row items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
										>
											Status:
										</TypographyLabel>
										{mapStatus(metadataEntry) == 'Pending' ? (
											<CalloutWarningIcon />
										) : (
											<CalloutSuccessIcon />
										)}
										<TypographyLabel size="medium">
											{mapStatus(metadataEntry)}
										</TypographyLabel>
									</div>
								)}
								{metadataEntry.template.slug !== 'proofOfExhibition' &&
									metadataEntry.template.slug !== 'offchainLend' && (
										<div className="mt-4 flex flex-row items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
											<TypographyLabel
												size="medium"
												isBold={true}
												additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
											>
												Status:
											</TypographyLabel>
											{!metadataEntry.isRevoked ? (
												<CalloutSuccessIcon />
											) : (
												<CalloutWarningIcon />
											)}
											<TypographyLabel size="medium">
												{!metadataEntry.isRevoked ? 'Completed' : 'Revoked'}
											</TypographyLabel>
										</div>
									)}
								<div className="md:hidden">
									<div className="mt-4 flex w-full flex-col gap-y-2">
										<Link
											to={getShareAttestationToXURL(
												metadataEntry,
												atomicLoreUrl,
												nftData,
											)}
											target="_blank"
										>
											<Button
												variant="secondary"
												leftIcon={<XIcon />}
												additionalClasses="w-full"
											>
												Share
											</Button>
										</Link>
										<InternalLink to={getNFTProfileURL(nftData)}>
											<Button variant="primary" additionalClasses="w-full">
												View token profile
											</Button>
										</InternalLink>
										{metadataEntry.properties &&
											metadataEntry.properties.rollupAttestationCID && (
												<InternalLink
													to={`/metadataProfile/rollup/${metadataEntry.properties.rollupAttestationCID}`}
													prefetch="none"
												>
													<Button variant="primary">
														View roll-up attestation
													</Button>
												</InternalLink>
											)}
									</div>
								</div>
								{!metadataEntry.isRevoked && (
									<>
										<div className="mt-16 flex h-full w-full flex-col rounded border border-textInactiveLight bg-uiTertiaryLight dark:border-textInactiveDark dark:bg-uiTertiaryDark">
											{metadataEntry.template.slug === 'proofOfExhibition' && (
												<ProofOfExhibitionTemplate
													proofOfExhibition={metadataEntry}
												/>
											)}
											{metadataEntry.template.slug === 'comment' && (
												<CommentTemplate comment={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'mediaCitation' && (
												<MediaCitationTemplate mediaCitation={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'media' && (
												<MediaUploadTemplate mediaUpload={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'tweet' && (
												<TweetTemplate tweet={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'cast' && (
												<CastTemplate cast={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'instagram' && (
												<InstagramTemplate instagram={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'offchainLend' && (
												<OffchainLendTemplate offchainLend={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'offchainSale' && (
												<OffchainSaleTemplate offchainSale={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'revoke' && (
												<RevokeTemplate revoke={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'countersign' && (
												<CountersignTemplate countersign={metadataEntry} />
											)}
											{metadataEntry.template.slug === 'bulk' && (
												<BulkTemplate bulk={metadataEntry} />
											)}
										</div>
										{rollupAttestation && (
											<div className="mt-16 flex flex-col">
												<TypographyHeading
													size="medium"
													isBold={true}
													additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
												>
													Event
												</TypographyHeading>
												<InternalLink to={getEventURL(rollupAttestation)}>
													<ContentTile
														title={rollupAttestation?.event.eventTitle}
														description={`Event • Rollup`}
														image={rollupAttestation?.event.eventImageUrl}
														isVerified={true}
														showCaret={true}
														showBorder={false}
														variant="art"
													/>
												</InternalLink>
											</div>
										)}
										<div className="mt-16 flex flex-1 items-center gap-x-4">
											<TypographyHeading
												size="medium"
												isBold={true}
												additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
											>
												Associated NFTs
											</TypographyHeading>
											<Counter size="small" children={1} />
										</div>
										<div className="pt-6">
											<CIDsTable
												data={[
													{
														cid: cid,
														name: nftData.name,
														format:
															nftData.image && nftData.image?.contentType
																? nftData.image?.contentType
																: 'Unknown format',
														date: nftData.timeLastUpdated
															? nftData.timeLastUpdated
															: '',
														src: nftData.previewImage
															? nftData.previewImage
															: '',
														isVerified: false,
														chain: nftData.chain ? nftData.chain : '',
														contractAddress: nftData.contract
															? nftData.contract.address
															: '',
														tokenId: nftData.tokenId,
														status: mapStatus(metadataEntry),
														attestation: metadataEntry,
													},
												]}
											></CIDsTable>
										</div>
										<div className="mt-16 flex flex-1 items-center gap-x-4">
											<TypographyHeading
												size="medium"
												isBold={true}
												additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
											>
												Signers
											</TypographyHeading>
											<Counter
												size="small"
												children={mapSigners(metadataEntry).length}
											/>
										</div>
										<div className="pt-6">
											<SignersTable
												data={mapSigners(metadataEntry)}
											></SignersTable>
										</div>
									</>
								)}
							</>
						)
					}}
				</Await>
			</Suspense>
		</div>
	)
}
